import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CreateFieldDefinition,
  CreateFieldDefinitionSchema,
  FIELD_TYPES,
  type FieldDefinition,
  type FieldType,
  INDEXABLE_TYPES,
  parseFieldConfig,
  type UpdateFieldDefinition,
  UpdateFieldDefinitionSchema,
} from '@servicetim/shared';
import { useCreateField, useProjects, useTenantRoles, useUpdateField } from '@servicetim/ui/api';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@servicetim/ui/components';
import { useId, useMemo } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import type { z } from 'zod';
import type { CheckboxListOption } from '../common/checkbox-list';
import { FormDialog } from '../common/form-dialog';
import { FormField, fieldA11y } from '../common/form-field';
import { useFormSubmit } from '../common/use-form-submit';
import { FieldConfigForm, FieldFlags } from './field-config-form';
import { defaultConfigFor, FIELD_TYPE_LABELS, stripConfigType } from './field-type-labels';

const emptyToNull = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? null : value;

/** Options for the `restrictToRoleIds` / `allowedProjectIds` lists of the config sub-form. */
function useConfigOptions() {
  const roles = useTenantRoles();
  const projects = useProjects({ pageSize: 200 });
  const roleOptions = useMemo<CheckboxListOption[]>(
    () => (roles.data ?? []).map((r) => ({ id: r.id, label: r.name, description: r.key })),
    [roles.data],
  );
  const projectOptions = useMemo<CheckboxListOption[]>(
    () => projects.items.map((p) => ({ id: p.id, label: p.name, description: p.key })),
    [projects.items],
  );
  return { roleOptions, projectOptions };
}

/** Label + description shared by create and edit. */
function LabelFields({ id }: { id: string }) {
  const {
    register,
    formState: { errors },
  } = useFormContext<{ label: string; description: string | null }>();
  return (
    <>
      <FormField label="Label" htmlFor={`${id}-label`} required error={errors.label?.message}>
        <Input
          {...register('label')}
          {...fieldA11y(`${id}-label`, { error: errors.label })}
          maxLength={80}
        />
      </FormField>
      <FormField
        label="Description"
        htmlFor={`${id}-description`}
        description="Shown as help text under the field."
        error={errors.description?.message}
      >
        <Textarea
          rows={2}
          maxLength={500}
          {...register('description', { setValueAs: emptyToNull })}
          {...fieldA11y(`${id}-description`, { description: true, error: errors.description })}
        />
      </FormField>
    </>
  );
}

export function FieldDialog({
  field,
  onClose,
}: {
  field: FieldDefinition | null;
  onClose: () => void;
}) {
  return field ? (
    <EditFieldDialog field={field} onClose={onClose} />
  ) : (
    <CreateFieldDialog onClose={onClose} />
  );
}

/* ----------------------------- create ----------------------------- */

type CreateInput = z.input<typeof CreateFieldDefinitionSchema>;

function CreateFieldDialog({ onClose }: { onClose: () => void }) {
  const id = useId();
  const create = useCreateField();
  const { roleOptions, projectOptions } = useConfigOptions();
  const form = useForm<CreateInput, unknown, CreateFieldDefinition>({
    resolver: zodResolver(CreateFieldDefinitionSchema),
    defaultValues: {
      type: 'text',
      label: '',
      description: null,
      config: defaultConfigFor('text'),
      indexed: false,
      enforceUnique: false,
    },
  });
  const submit = useFormSubmit(form);
  const { errors } = form.formState;
  const type = form.watch('type');

  const changeType = (next: FieldType) => {
    form.setValue('type', next, { shouldDirty: true });
    form.setValue('config', defaultConfigFor(next), { shouldDirty: true });
    form.clearErrors('config');
    if (!INDEXABLE_TYPES.has(next)) {
      form.setValue('indexed', false);
      form.setValue('enforceUnique', false);
    }
  };

  return (
    <FormProvider {...form}>
      <FormDialog
        open
        onOpenChange={(open) => (open ? undefined : onClose())}
        title="New tenant field"
        description="Shared by every project; the key is generated and never changes. Projects bind it from their field settings."
        formId={`${id}-form`}
        submitLabel="Create field"
        size="lg"
        busy={submit.busy}
        error={submit.error}
        omitPaths={submit.omitPaths}
        onSubmit={form.handleSubmit(async (values) => {
          const created = await submit.run(() =>
            create.mutateAsync({ ...values, config: stripConfigType(values.config) }),
          );
          if (created) {
            toast.success(`Field ${created.label} created (${created.key})`);
            onClose();
          }
        })}
      >
        <FormField label="Type" htmlFor={`${id}-type`} required error={errors.type?.message}>
          <Controller
            control={form.control}
            name="type"
            render={({ field }) => (
              <Select value={field.value} onValueChange={(v) => changeType(v as FieldType)}>
                <SelectTrigger {...fieldA11y(`${id}-type`, { error: errors.type })}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {FIELD_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </FormField>
        <LabelFields id={id} />
        <fieldset className="space-y-3 rounded-md border border-border p-3">
          <legend className="px-1 font-medium text-fg-muted text-xs uppercase tracking-wide">
            {FIELD_TYPE_LABELS[type]} settings
          </legend>
          <FieldConfigForm type={type} roleOptions={roleOptions} projectOptions={projectOptions} />
        </fieldset>
        <FieldFlags type={type} />
      </FormDialog>
    </FormProvider>
  );
}

/* ----------------------------- edit ----------------------------- */

/** Update schema plus the config/index rules the API applies against the stored type. */
const editSchemaFor = (type: FieldType) =>
  UpdateFieldDefinitionSchema.superRefine((value, ctx) => {
    if (value.config !== undefined) {
      const parsed = parseFieldConfig(type, value.config);
      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          ctx.addIssue({ code: 'custom', message: issue.message, path: ['config', ...issue.path] });
        }
      }
    }
    if (value.indexed && !INDEXABLE_TYPES.has(type)) {
      ctx.addIssue({
        code: 'custom',
        message: `${type} fields cannot be indexed`,
        path: ['indexed'],
      });
    }
    if (value.enforceUnique && !value.indexed) {
      ctx.addIssue({
        code: 'custom',
        message: 'enforceUnique requires indexed',
        path: ['enforceUnique'],
      });
    }
  });

type EditInput = z.input<typeof UpdateFieldDefinitionSchema>;

function EditFieldDialog({ field, onClose }: { field: FieldDefinition; onClose: () => void }) {
  const id = useId();
  const update = useUpdateField();
  const { roleOptions, projectOptions } = useConfigOptions();
  const schema = useMemo(() => editSchemaFor(field.type), [field.type]);
  const form = useForm<EditInput, unknown, UpdateFieldDefinition>({
    resolver: zodResolver(schema),
    defaultValues: {
      label: field.label,
      description: field.description,
      config: stripConfigType(field.config as Record<string, unknown>),
      indexed: field.indexed,
      enforceUnique: field.enforceUnique,
    },
  });
  const submit = useFormSubmit(form);

  return (
    <FormProvider {...form}>
      <FormDialog
        open
        onOpenChange={(open) => (open ? undefined : onClose())}
        title={`Edit ${field.label}`}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <code className="rounded bg-surface-sunken px-1 font-mono text-xs">{field.key}</code>
            {FIELD_TYPE_LABELS[field.type]} · the key and type are fixed here; use "Change type" for
            whitelisted widenings.
          </span>
        }
        formId={`${id}-form`}
        size="lg"
        busy={submit.busy}
        error={submit.error}
        omitPaths={submit.omitPaths}
        onSubmit={form.handleSubmit(async (patch) => {
          const saved = await submit.run(() => update.mutateAsync({ id: field.id, patch }));
          if (saved) {
            toast.success(`Field ${saved.label} updated`);
            onClose();
          }
        })}
      >
        <LabelFields id={id} />
        <fieldset className="space-y-3 rounded-md border border-border p-3">
          <legend className="px-1 font-medium text-fg-muted text-xs uppercase tracking-wide">
            {FIELD_TYPE_LABELS[field.type]} settings
          </legend>
          <FieldConfigForm
            type={field.type}
            roleOptions={roleOptions}
            projectOptions={projectOptions}
          />
        </fieldset>
        <FieldFlags type={field.type} />
      </FormDialog>
    </FormProvider>
  );
}
